import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Activity, TrendingUp, TrendingDown, MessageSquare, Users, Building2,
  Bookmark, BadgeCheck, MapPin, Eye, Loader2, ArrowUpRight, Send, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const fmtInt = (v) => new Intl.NumberFormat('en-US').format(Number(v) || 0);
const fmtMoney = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    .format(Number(v) || 0);

const Stat = ({ label, value, hint, icon: Icon, tone = 'blue', trend, testId }) => {
  const tones = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-100',
    violet: 'bg-violet-50 text-violet-700 border-violet-100',
  };
  const trendUp = typeof trend === 'number' && trend > 0;
  const trendDown = typeof trend === 'number' && trend < 0;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        {typeof trend === 'number' && (
          <span className={`text-xs font-medium inline-flex items-center gap-1 ${
            trendUp ? 'text-emerald-600' : trendDown ? 'text-red-600' : 'text-slate-500'
          }`}>
            {trendUp ? <TrendingUp className="h-3.5 w-3.5" /> :
             trendDown ? <TrendingDown className="h-3.5 w-3.5" /> : null}
            {trend > 0 ? `+${trend}%` : `${trend}%`}
          </span>
        )}
      </div>
      <div className="text-3xl font-bold text-slate-900 tabular-nums">{value}</div>
      <div className="text-sm text-slate-600 mt-1">{label}</div>
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  );
};

export default function LiquidityDashboard({ token }) {
  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  const [data, setData] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [liq, sch] = await Promise.all([
          axios.get(`${API}/admin/liquidity`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/admin/scheduler/status`, { headers: { Authorization: `Bearer ${token}` } })
            .catch(() => ({ data: null })),
        ]);
        setData(liq.data);
        setScheduler(sch.data);
      } catch (err) {
        toast.error('Failed to load liquidity dashboard');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-8" data-testid="liquidity-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="h-6 w-6 text-blue-700" />
            Marketplace Liquidity
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Supply, demand and engagement — updated in real time.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={async () => {
              const confirmed = window.confirm('Send weekly investor check-in emails to all investors with target markets set?');
              if (!confirmed) return;
              try {
                const res = await axios.post(`${API}/admin/digest/investor-checkin/send`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success(`Check-in sent to ${res.data.sent} investor${res.data.sent === 1 ? '' : 's'}. ${res.data.skipped} skipped.`);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to send check-in');
              }
            }}
            className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-slate-700 text-sm font-medium px-4 py-2 rounded-full transition-colors"
            data-testid="send-investor-checkin-btn"
          >
            <Users className="h-4 w-4" />
            Send investor check-in
          </button>
          <button
            type="button"
            onClick={async () => {
              const confirmed = window.confirm('Send saved-search digest emails to all users with matching new deals?');
              if (!confirmed) return;
              try {
                const res = await axios.post(`${API}/admin/digest/send-weekly`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success(`Digest sent to ${res.data.sent} user${res.data.sent === 1 ? '' : 's'}. ${res.data.skipped} skipped.`);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to send digest');
              }
            }}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-full transition-colors"
            data-testid="send-weekly-digest-btn"
          >
            <Send className="h-4 w-4" />
            Send saved-search digest
          </button>
        </div>
      </div>

      {/* Supply row */}
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Supply</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat
            label="Deals posted this week"
            value={fmtInt(data.supply.deals_this_week)}
            hint={`vs ${fmtInt(data.supply.deals_prev_week)} previous week`}
            icon={Building2}
            tone="blue"
            trend={data.supply.week_over_week_change_pct}
            testId="stat-deals-week"
          />
          <Stat
            label="Deals this month"
            value={fmtInt(data.supply.deals_this_month)}
            icon={Building2}
            tone="blue"
            testId="stat-deals-month"
          />
          <Stat
            label="Total active deals"
            value={fmtInt(data.supply.total_active_deals)}
            icon={Building2}
            tone="slate"
            testId="stat-deals-total"
          />
          <Stat
            label="Verified wholesalers"
            value={fmtInt(data.users.verified_users)}
            icon={BadgeCheck}
            tone="violet"
            testId="stat-verified"
          />
        </div>
      </section>

      {/* Demand row */}
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Demand</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat
            label="Inquiries per deal"
            value={data.demand.inquiries_per_deal}
            hint={`${fmtInt(data.demand.total_inquiries)} inquiries lifetime`}
            icon={ArrowUpRight}
            tone="green"
            testId="stat-inquiries-per-deal"
          />
          <Stat
            label="Inquiries this week"
            value={fmtInt(data.demand.inquiries_this_week)}
            icon={ArrowUpRight}
            tone="green"
            testId="stat-inquiries-week"
          />
          <Stat
            label="Messages this week"
            value={fmtInt(data.demand.messages_this_week)}
            hint={`${fmtInt(data.demand.conversations_this_week)} new conversations`}
            icon={MessageSquare}
            tone="amber"
            testId="stat-messages-week"
          />
          <Stat
            label="Saved searches"
            value={fmtInt(data.signals.total_saved_searches)}
            hint={`+${fmtInt(data.signals.saved_searches_this_week)} this week`}
            icon={Bookmark}
            tone="violet"
            testId="stat-saved-searches"
          />
        </div>
      </section>

      {/* Users row */}
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Users</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat
            label="Total users"
            value={fmtInt(data.users.total_users)}
            icon={Users}
            tone="blue"
            testId="stat-users-total"
          />
          <Stat
            label="New users this week"
            value={fmtInt(data.users.new_users_this_week)}
            icon={Users}
            tone="green"
            testId="stat-users-new"
          />
          <Stat
            label="Active users (30d)"
            value={fmtInt(data.users.active_users_30d)}
            hint="posted a deal or sent a message"
            icon={Activity}
            tone="amber"
            testId="stat-users-active"
          />
          <Stat
            label="Verification rate"
            value={
              data.users.total_users > 0
                ? `${((data.users.verified_users / data.users.total_users) * 100).toFixed(1)}%`
                : '0%'
            }
            icon={BadgeCheck}
            tone="violet"
            testId="stat-verified-rate"
          />
        </div>
      </section>

      {/* Scheduler status */}
      {scheduler && scheduler.jobs && scheduler.jobs.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="scheduler-status">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-blue-700" />
            <h3 className="font-semibold text-slate-900">Scheduled weekly emails</h3>
            <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${
              scheduler.running ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500'
            }`}>
              {scheduler.running ? 'Running' : 'Stopped'}
            </span>
          </div>
          <div className="text-xs text-slate-500 mb-3">Timezone: {scheduler.timezone}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {scheduler.jobs.map((j) => {
              const nextDt = j.next_run ? new Date(j.next_run) : null;
              const label = j.id === 'investor_checkin' ? 'Investor check-in' :
                            j.id === 'saved_search_digest' ? 'Saved-search digest' : j.id;
              return (
                <div key={j.id} className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-sm font-medium text-slate-900">{label}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Next run: {nextDt ? nextDt.toLocaleString(undefined, {
                      weekday: 'short', month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit'
                    }) : '—'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top markets + top deals */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <MapPin className="h-4 w-4 text-blue-700" /> Top markets by supply
          </h3>
          {data.top_markets.length === 0 ? (
            <p className="text-sm text-slate-400">No markets yet.</p>
          ) : (
            <div className="space-y-3">
              {data.top_markets.map((m, i) => (
                <div
                  key={`${m.city}-${m.state}-${i}`}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  data-testid={`liq-market-row-${i}`}
                >
                  <div>
                    <div className="font-medium text-slate-900">{m.city || 'Unknown'}, {m.state || ''}</div>
                    <div className="text-xs text-slate-500">Avg price {fmtMoney(m.avg_price)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{m.deal_count}</div>
                    <div className="text-xs text-slate-400">deals</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <Eye className="h-4 w-4 text-blue-700" /> Most-viewed deals this week
          </h3>
          {data.top_deals_this_week.length === 0 ? (
            <p className="text-sm text-slate-400">No deal views yet this week.</p>
          ) : (
            <div className="space-y-3">
              {data.top_deals_this_week.map((d, i) => (
                <a
                  key={d.deal_id}
                  href={`/deals/${d.deal_id}`}
                  className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                  data-testid={`liq-top-deal-${i}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{d.title}</div>
                    <div className="text-xs text-slate-500">
                      {d.city}, {d.state} · {fmtMoney(d.asking_price)}
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{d.views_this_week}</div>
                    <div className="text-xs text-slate-400">views</div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
